import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './MyProfile.module.scss';
import { Icon } from '../../../../components/Icon';
import { useUserContext } from '../../../../context/UserContext';
import { CustomInput } from '../../../../components/Input/Input';
import { AvatarEditorModal } from '../AvatarEditor/AvatarEditor';
import Modal from '../../../../components/Modal';
import CmsAPI from '../../../../../common/apis/CmsAPI';
import config from '../../../../../../conf';
import { Auth } from 'aws-amplify';
import cx from 'classnames';
import Toast from '../../../../../common/utils/Toast';
import { CustomButton } from '../../../../components/Button/Button';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import AuthorizationService from '../../../../service/AuthorizationService';

import { useTranslation } from 'react-i18next';
import { ToastNotification } from '../../../../components/Toast/Toast';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const initPasswordField = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const getInvalidPasswordMessage = (t) => (
  <div className={styles.passwordGuidelines}>
    <p>{t('reset_password.password.weak_or_compromised')}</p>
    <ul>
      <li>{t('reset_password.password.reuse_not_allowed')}</li>
      <li>{t('reset_password.password.avoid_common_patterns')}</li>
      <li>{t('reset_password.password.use_passphrase')}</li>
    </ul>
  </div>
);

export const MyProfile = () => {
  const { user, updateUserData } = useUserContext();
  const [isSsoUser, setIsSsoUser] = useState(true);
  const [isValidPassFields, setIsValidPassFields] = useState({ empty: false, meetsRequirements: false });
  const [isValidNameFields, setIsValidNameFields] = useState(true);
  const [passwordValidation, setPasswordValidation] = useState(false);
  const [avatarModal, setAvatarModal] = useState(false);
  const [isShownUpload, setIsShownUpload] = useState(false);
  const [reqTenChars, setReqTenChars] = useState(false);
  const [reqLowerUpper, setReqLowerUpper] = useState(false);
  const [reqSymbol, setReqSymbol] = useState(false);
  const [reqNumber, setReqNumber] = useState(false);
  const [currentPasswordValid, setCurrentPasswordValid] = useState(true);
  const [currentPasswordMessage, setCurrentPasswordMessage] = useState('');
  const [confirmationDeleteAvatar, setConfirmationDeleteAvatar] = useState(false);

  const [validateMessage, setValidateMessage] = useState('');
  const [isChanged, setIsChanged] = useState(false);

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const { t } = useTranslation();

  const history = useHistory();

  const reg = /(?=.*[0-9])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!@#$%^&*]{6,}/g;

  const { firstName, lastName, avatarUri, userEmail } = user;

  const [profileForm, setProfileForm] = useState({
    firstName,
    lastName,
  });

  const [profileAvatar, setProfileAvatar] = useState(avatarUri ? avatarUri : '');

  const [passwordForm, setPasswordForm] = useState(initPasswordField);

  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  useMemo(() => {
    const empty = Object.keys(passwordForm).some(value => !passwordForm[value]);
    const meetsRequirements = Object.keys(passwordForm).every(value => Boolean(new RegExp(reg).test(passwordForm[value])));
    setIsValidPassFields({ empty, meetsRequirements });
  }, [passwordForm]);

  useMemo(() => {
    const fields = Object.keys(profileForm).some(value => !profileForm[value]);
    if ((profileForm.firstName !== firstName || profileForm.lastName !== lastName) && !fields) {
      setIsChanged(true);
      if (/^((\S)+.*)$/.test(profileForm.firstName) && /^((\S)+.*)$/.test(profileForm.lastName)) {
        setIsValidNameFields(true);
      } else {
        //setIsChanged(false);
        setIsValidNameFields(false);
      }
    } else {
      setIsChanged(false);
      setIsValidNameFields(false);
    }
  }, [profileForm]);

  useEffect(() => {
    checkIsSsoUser();
  }, []);

  const checkIsSsoUser = async () => {
    try {
      const response = await AuthorizationService.isSsoUser();
      console.log('isSsoUser', response);
      setIsSsoUser(response);
    } catch (error) {
      console.error(error);
      setIsSsoUser(false);
    }
  };
  useEffect(() => {
    setProfileForm({ firstName: user.firstName, lastName: user.lastName });
  }, [user]);

  const handleChangeProfile = useCallback(
    (value, field) => {
      setProfileForm({ ...profileForm, [field]: value });
    },
    [profileForm]
  );

  const handleChangePassword = useCallback(
    (value, field) => {
      setPasswordForm(prevState => ({ ...prevState, [field]: value }));

      if (field === 'newPassword') {
        // At least 10 characters
        if (value.length >= config.general.minPasswordLength) {
          setReqTenChars(true);
        } else {
          setReqTenChars(false);
        }

        // At least one lower and one uppercase character
        if (/(?=.*[a-z])(?=.*[A-Z])/.test(value)) {
          setReqLowerUpper(true);
        } else {
          setReqLowerUpper(false);
        }

        // At least one symbol
        if (/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value)) {
          setReqSymbol(true);
        } else {
          setReqSymbol(false);
        }

        // At least one number
        if (/\d/.test(value)) {
          setReqNumber(true);
        } else {
          setReqNumber(false);
        }
      }
    },
    [passwordForm]
  );

  useEffect(() => {
    let count = 0;
    setPasswordValidation(false);
    count += reg.test(passwordForm.newPassword) ? 1 : 0;
  }, [passwordForm]);

  function updateUserDetails(key, value) {
    const currentUserDetails = JSON.parse(localStorage.getItem('userDetails'));
    let newUserDetails = currentUserDetails;
    newUserDetails[key] = value;
    localStorage.setItem('userDetails', JSON.stringify(newUserDetails));
  }

  const handleSave = () => {
    // Handle password change
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setValidateMessage(t('account.settings.profile.password_not_match'));
      return setPasswordValidation(true);
    } else if (passwordForm.newPassword === passwordForm.currentPassword) {
      setValidateMessage(t('account.settings.profile.passwords_the_same'));
      return setPasswordValidation(true);
    }
    setPasswordValidation(false);

    if (
      passwordForm.currentPassword.length >= config.general.minPasswordLength &&
      reqTenChars &&
      reqLowerUpper &&
      reqSymbol &&
      reqNumber
    ) {
      // newPassword and confirmPassword are identical, current password is set, and all password requirements are met
      Auth.currentAuthenticatedUser()
        .then(user => {
          return Auth.changePassword(user, passwordForm.currentPassword, passwordForm.newPassword);
        })
        .then(data => {
          setPasswordForm(initPasswordField);
          ToastNotification({
            message: t('account.settings.profile.changes_successfully'),
            iconName: 'success',
            isMobileView: isMobileView,
          });
          console.log(data);
        })
        .catch(err => {
          console.log(err);
          if (err.message.includes('Incorrect')) {
            setCurrentPasswordMessage(t('account.settings.profile.invalid_current_password'));
            setCurrentPasswordValid(false);
          } else if (err.name === 'InvalidPasswordException') {
            setValidateMessage(getInvalidPasswordMessage(t));
            setPasswordValidation(true);
          } else {
            setValidateMessage(t('reset_password.password.generic_error'));
            setPasswordValidation(true);
          }
        });
    }
  };

  const validateCurrentPassword = () => {
    const valid = reg.test(passwordForm.currentPassword);
    setCurrentPasswordValid(valid);
    if (!valid) {
      setCurrentPasswordMessage(t('account.settings.profile.password_requirements'));
    }
  };

  const handleSaveName = async () => {
    // Handle name change
    const firstName = profileForm.firstName
      .replace(/^\s{1,}/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const lastName = profileForm.lastName.replace(/\s{2,}/g, ' ').trim();
    const fullName = `${firstName} ${lastName}`;
    try {
      const response = await CmsAPI.updateUser({ name: fullName });
      if (response.result == true) {
        setIsValidNameFields(true);
        ToastNotification({
          message: t('account.settings.profile.changes_successfully'),
          iconName: 'success',
          isMobileView: isMobileView,
        });
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const currentSession = await Auth.currentSession();
        cognitoUser.refreshSession(currentSession.refreshToken, () => {
          updateUserData();
        });

        // If name change successful, also update name in userDetails in localStorage
        updateUserDetails('name', fullName);
      }
    } catch (e) {
      Toast.error(t('account.settings.profile.change_username_error'), e);
    }
  };

  const handleCancelName = () => {
    setIsChanged(false);
    setProfileForm({
      firstName,
      lastName,
    });
  };

  const handleCancelPassword = () => {
    setPasswordForm(initPasswordField);
  };

  const handleOpenModal = () => {
    setAvatarModal(true);
  };

  const handleCloseModal = () => {
    setAvatarModal(false);
  };

  const handleChangeAvatar = img => {
    setProfileAvatar(img);
  };

  const handleDeleteAvatar = async () => {
    try {
      await CmsAPI.updateUser({ avatar: null });
      await updateUserData();
      setProfileAvatar('');
      setConfirmationDeleteAvatar(false);
    } catch (e) {
      setConfirmationDeleteAvatar(false);
      console.log('avatar change error', e);
    }
  };

  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const forgotPasswordClick = () => {
    CmsAPI.forgotPassword(userEmail)
      .then(response => {
        setForgotPasswordLoading(false);
        ToastNotification({
          message: t('account.settings.password.reset_password.email'),
          iconName: 'letter',
          isMobileView: isMobileView,
        });
      })
      .catch(e => {
        console.error(e);
        setForgotPasswordLoading(false);
        Toast.error(t('account.settings.profile.reset_password_error'));
      });
    setForgotPasswordLoading(true);
  };

  const handleDeleteAccount = async () => {
    const confirmDelete = confirm(t('account.settings.account.delete.confirm_message'));
    console.log('confirmDelete', confirmDelete);
    if (confirmDelete) {
      try {
        await CmsAPI.deleteUser();
        history.push('/logout');
      } catch (e) {
        Toast.error(t('account.settings.profile.delete_account_error'));
        console.log('delete account error', e);
      }
    }
  };

  useEffect(() => {
    const requirementsMet = reqTenChars && reqLowerUpper && reqSymbol && reqNumber;
    setPasswordRequirementsMet(requirementsMet);

    const doPasswordsMatch = passwordForm.newPassword === passwordForm.confirmPassword
      && passwordForm.newPassword !== ''
      && passwordForm.newPassword !== passwordForm.currentPassword;
    setPasswordsMatch(doPasswordsMatch);
  }, [passwordForm, reqTenChars, reqLowerUpper, reqSymbol, reqNumber]);

  return (
    <div className={styles.settingsContainer}>
      <Modal className={styles.avatarModal} onClose={handleCloseModal} open={avatarModal} title="Modal">
        <AvatarEditorModal handleCloseModal={handleCloseModal} handleChangeAvatar={handleChangeAvatar} isMobileView={isMobileView} />
      </Modal>
      <div className={styles.profileContainer}>
        <div className={`${styles.profileTitle} ${styles.title}`}>{t('account.settings.profile')}</div>
        <div className={styles.profileSettings}>
          <div className={styles.profileFields}>
            <CustomInput
              type="text"
              fieldName={t('account.settings.profile.first_name')}
              placeholder={t('account.settings.profile.first_name')}
              value={profileForm.firstName}
              onChange={event => handleChangeProfile(event.target.value, 'firstName')}
              maxLength={100}
            />
            <CustomInput
              type="text"
              fieldName={t('account.settings.profile.last_name')}
              placeholder={t('account.settings.profile.last_name')}
              value={profileForm.lastName ? profileForm.lastName : ''}
              onChange={event => handleChangeProfile(event.target.value, 'lastName')}
              maxLength={100}
            />
            {!isMobileView && (
              <div className={styles.buttonContainer}>
                <CustomButton
                  label={t('general.save')}
                  variant="primary"
                  className={styles.pageButtons}
                  disabled={!isValidNameFields}
                  onClick={handleSaveName}
                />
                {isChanged && (
                  <CustomButton
                    label={t('general.cancel')}
                    variant="secondary"
                    onClick={handleCancelName}
                    className={styles.pageButtons}
                  />
                )}
              </div>
            )}
          </div>
          <div className={styles.pictureContainer}>
            <p> {t('account.settings.profile.profile_picture')}</p>
            <div className={styles.pictureWrapper}>
              <div
                className={`${!profileAvatar ? styles.avatarImage : styles.uploadedImg} ${isShownUpload ? styles.addProfilePicture : styles.defaultAvatar
                  }`}
                onClick={handleOpenModal}
                onMouseEnter={() => setIsShownUpload(true)}
                onMouseLeave={() => setIsShownUpload(false)}
              >
                {profileAvatar && !isShownUpload && (
                  <img
                    src={profileAvatar}
                    className={styles.avatar}
                    onMouseEnter={() => setIsShownUpload(true)}
                    onMouseLeave={() => setIsShownUpload(false)}
                    alt={t('clip_organizer.add_modal.avatar')}
                  />
                )}
                {!profileAvatar && !isShownUpload && (
                  <>
                    <div className={styles.letter}>{profileForm.firstName.charAt(0).toUpperCase()}</div>
                  </>
                )}
                {isShownUpload && (
                  <>
                    <Icon src="photo" className={styles.photoIcon} />
                  </>
                )}
              </div>
              {profileAvatar && (
                <CustomButton
                  variant="primary"
                  className={styles.deleteAvatarBtn}
                  onClick={() => setConfirmationDeleteAvatar(true)}
                  customStyles={{ height: '24px', minWidth: '24px', padding: '4px' }}
                  ariaLabel={t('general.close')}
                >
                  <Icon src="close_white" className={styles.closeIcon} />
                </CustomButton>
              )}
            </div>
            {isMobileView && (
              <div className={styles.buttonContainer}>
                <CustomButton
                  label={t('general.save')}
                  variant="primary"
                  className={styles.pageButtons}
                  disabled={!isValidNameFields}
                  onClick={handleSaveName}
                />
                {isValidNameFields && (
                  <CustomButton
                    label={t('general.cancel')}
                    variant="secondary"
                    onClick={handleCancelName}
                    className={styles.pageButtons}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!isSsoUser && (
        <div className={styles.passwordContainer}>
          <div className={`${styles.passwordTitle} ${styles.title}`}>{t('account.settings.password.title')}</div>
          <p>{t('account.settings.password.subtitle')}</p>
          <div className={styles.passwordFields}>
            <div className={styles.topContainer}>
              <CustomInput
                isShow
                type="password"
                fieldName={t('account.settings.password.current_password.label')}
                placeholder={t('account.settings.password.current_password.value')}
                value={passwordForm.currentPassword}
                onChange={event => handleChangePassword(event.target.value, 'currentPassword')}
                onFocusChanged={validateCurrentPassword}
                validateMessage={currentPasswordMessage}
                invalid={!currentPasswordValid}
              />
            </div>
            <div className={styles.bottomContainer}>
              <CustomInput
                isShow
                type="password"
                fieldName={t('account.settings.password.new_password.label')}
                placeholder={t('account.settings.password.new_password.value')}
                invalid={passwordValidation}
                value={passwordForm.newPassword}
                onChange={event => handleChangePassword(event.target.value, 'newPassword')}
              />
              <CustomInput
                isShow
                type="password"
                fieldName={t('account.settings.password.confirm_password.label')}
                placeholder={t('account.settings.password.confirm_password.value')}
                validateMessage={validateMessage}
                value={passwordForm.confirmPassword}
                invalid={passwordValidation}
                onChange={event => handleChangePassword(event.target.value, 'confirmPassword')}
              />
            </div>
          </div>

          <div
            className={cx(styles.passwordRequirements, {
              hidden: reqTenChars && reqLowerUpper && reqSymbol && reqNumber,
            })}
          >
            <ul>
              <li className={cx({ hidden: reqTenChars })}>
                {`${t('general.password.requirement_one')} ${config.general.minPasswordLength} ${t(
                  'general.password.requirement_one_characters'
                )}`}
              </li>
              <li className={cx({ hidden: reqLowerUpper })}>{t('general.password.requirement_two')}</li>
              <li className={cx({ hidden: reqSymbol })}>{t('general.password.requirement_three')}</li>
              <li className={cx({ hidden: reqNumber })}>{t('general.password.requirement_four')}</li>
            </ul>
          </div>

          <div className={styles.buttonContainer}>
            <CustomButton
              label={t('general.save')}
              variant="primary"
              disabled={!passwordRequirementsMet || !passwordsMatch || !passwordForm.currentPassword}
              onClick={handleSave}
              className={styles.pageButtons}
            />
            {!isValidPassFields.empty && (
              <CustomButton
                label={t('general.cancel')}
                variant="secondary"
                className={styles.pageButtons}
                onClick={handleCancelPassword}
              />
            )}
          </div>
        </div>
      )}
      {!isSsoUser && (
        <div className={styles.resetContainer}>
          <div className={`${styles.passwordTitle} ${styles.title}`}>{t('account.settings.password.reset_password.title')}</div>
          <p>{t('account.settings.password.reset_password.subtitle')}</p>
          <CustomButton
            label={t('account.settings.password.reset_password.button')}
            variant="secondary"
            onClick={forgotPasswordClick}
            disabled={forgotPasswordLoading}
          />
        </div>
      )}
      {!isSsoUser && (
        <div className={styles.resetContainer}>
          <div className={`${styles.passwordTitle} ${styles.title}`}>{t('account.settings.delete.title')}</div>
          <p>{t('account.settings.delete_account.subtitle')}</p>
          <CustomButton label={t('account.settings.delete_account.button')} variant="danger-outline" onClick={handleDeleteAccount} />
        </div>
      )}
      <ConfirmationModal
        message={t('account.settings.password.reset_password.confirmation_message')}
        src="bin_red"
        open={confirmationDeleteAvatar}
        cancelLabel={t('general.cancel')}
        confirmationLabel={t('video.settings.delete')}
        onClose={() => setConfirmationDeleteAvatar(false)}
        onConfirm={handleDeleteAvatar}
      />
    </div>
  );
};
